import {Button, ParagraphText} from '@thryvlabs/maverick'
import dayjs from 'dayjs'
import React, {useMemo} from 'react'
import {DateTimeModal} from '../date-time-modal'
import {Spinner} from '../loader-spinner'
import {useUserFeatures} from '../../context/user-features-context'
import {useUserRoles} from '../../utils/use-user-roles'
import {addDays} from 'date-fns'

const RADIO_LANGUAGE_PREFERENCES = [
  {name: 'Yes', value: true},
  {name: 'No', value: false},
]

const WFC_SKIP_ELIGIBLE_ROLES = new Set([
  'premisepilot',
  'premise',
  'directsales',
  'premisedemo',
  'reseller',
  'resellers',
])

const THRYV_PLAN_NAMES = new Set(['plus', 'professional', 'pro', 'unlimited'])

const normalizeRole = role =>
  String(role || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '')

const normalizePlanName = planName =>
  String(planName || '')
    .toLowerCase()
    .replace(/[()]/g, '')
    .trim()

const toValidDate = (value, fallback) => {
  const d = new Date(value)
  return isNaN(d.getTime()) ? fallback : d
}

export default function UnconfirmedCall({
  apptData,
  defaultCentersAndApps,
  selectedCentersAndApps,
  isLoading,
  shouldEnableCreativeCallModal,
  includeAddonsCodes,
  accountInformation,
  isRestrictedAccessHIPPASelected,
  noneCustomVideoCreativeItem,
  selectedService,
  selectedAddons,
  isMCSelected,
  isUsSpanishOptionAvailable,
  register,
  errors,
  apptId,
  defaultDate = () => {
    const date = new Date()
    date.setDate(date.getDate() + 2)
    return date
  },
  isThryvLeadsOrSeoIncluded,
  onboardingCallDate,
  isThryvLeads = false, // new field inclusion for tl mc
  isNewTLMCAccessible,
  keapAccount,
  seoBillingDate,
  newSEO4v,
  isMCAlreadyIncluded,
  pre5SeoOffsetStartDay,
}) {
  const {roles} = useUserRoles()
  const {
    Enable_Skip_Button_For_MC,
    Skip_Appointment: frontendSkipAppointment,
    Enable_BNI_Leads_Lift,
  } = useUserFeatures()

  const is5NewOrderFLow = useMemo(() => {
    if (!defaultCentersAndApps) return true

    const isZero = ({amount = 0, subtotal = 0, total = 0, unitAmount = 0}) =>
      amount + subtotal + total + unitAmount === 0

    return (
      (defaultCentersAndApps.centers || []).every(isZero) &&
      (defaultCentersAndApps.apps || []).every(isZero) &&
      (defaultCentersAndApps.oneTimes || []).every(isZero)
    )
  }, [defaultCentersAndApps])

  const isNewMC =
    selectedCentersAndApps?.centers?.some(({code}) => code.startsWith('mc_')) &&
    !defaultCentersAndApps?.centers?.some(({code}) => code.startsWith('mc_'))
  const isNewBC =
    selectedCentersAndApps?.centers?.some(({code}) => code.startsWith('bc')) &&
    !defaultCentersAndApps?.centers?.some(({code}) => code.startsWith('bc'))
  const isKeapTierCode = code =>
    code.startsWith('keap_max') ||
    code.startsWith('keap_ultimate') ||
    code.startsWith('keap_pro')
  const isNewKeapMax =
    selectedCentersAndApps?.centers?.some(({code}) => isKeapTierCode(code)) &&
    !defaultCentersAndApps?.centers?.some(({code}) => isKeapTierCode(code))
  const isNewHomeServices =
    selectedCentersAndApps?.centers?.some(({code}) => code.startsWith('max')) &&
    !defaultCentersAndApps?.centers?.some(({code}) => code.startsWith('max'))

  const isNewWFC =
    selectedCentersAndApps?.centers?.some(({code}) => code.startsWith('wc_')) &&
    !defaultCentersAndApps?.centers?.some(({code}) => code.startsWith('wc_'))

  const isLegacyWFCSelected =
    selectedAddons?.some(
      ({product, planCode, tempLineActionCode}) =>
        tempLineActionCode === 'New' &&
        (product?.includes('Workforce Center') ||
          planCode?.includes('_wc') ||
          planCode?.startsWith('wc_')),
    ) || false

  const hasLegacyThryvPlan =
    selectedAddons?.some(({type, product}) => {
      if (type !== 'selectedPlan') {
        return false
      }

      const normalizedPlanName = normalizePlanName(product)

      return Array.from(THRYV_PLAN_NAMES).some(planName =>
        normalizedPlanName.startsWith(planName),
      )
    }) ||
    Array.from(THRYV_PLAN_NAMES).some(planName =>
      normalizePlanName(accountInformation?.selectedPackage).startsWith(
        planName,
      ),
    )

  const isNewLeadLift = useMemo(() => {
    if (selectedCentersAndApps) {
      return selectedCentersAndApps?.apps?.some(({code}) => {
        if (code !== 'addon-5-leadslift-m2m') return false
        return !defaultCentersAndApps?.apps?.some(app => app?.code === code)
      })
    }
    return selectedAddons?.some(({planCode}) => {
      if (planCode !== 'addon-4-leadslift-m2m') {
        return false
      }
      return !accountInformation?.addOns?.some(
        app => app?.addOnCode === planCode,
      )
    })
  }, [selectedCentersAndApps, selectedAddons, accountInformation])

  const isNewLeadLiftBNI = useMemo(() => {
    if (!Enable_BNI_Leads_Lift) return false

    if (selectedCentersAndApps) {
      return selectedCentersAndApps?.apps?.some(({code}) => {
        if (code !== 'addon-5-leadslift-bni-m2m') return false
        return !defaultCentersAndApps?.apps?.some(app => app?.code === code)
      })
    }
    return selectedAddons?.some(({planCode}) => {
      if (planCode !== 'addon-5-leadslift-bni-m2m') {
        return false
      }
      return !accountInformation?.addOns?.some(
        app => app?.addOnCode === planCode,
      )
    })
  }, [
    selectedCentersAndApps,
    selectedAddons,
    accountInformation,
    Enable_BNI_Leads_Lift,
  ])

  const normalizedRoles = (roles || []).map(normalizeRole)
  const isPartnerOps = normalizedRoles.includes('partnerops')
  const hasSeoInOrder =
    selectedCentersAndApps?.centers?.some(
      ({name}) =>
        String(name || '')
          .trim()
          .toLowerCase() === 'aeo & seo',
    ) ||
    selectedCentersAndApps?.apps?.some(
      ({name}) =>
        String(name || '')
          .trim()
          .toLowerCase() === 'aeo & seo',
    ) ||
    false
  const hasAppointmentRequiredProduct =
    Boolean(isThryvLeadsOrSeoIncluded) || hasSeoInOrder
  const shouldRestrictPartnerOpsSkip =
    isPartnerOps &&
    (hasAppointmentRequiredProduct || isNewBC || isNewHomeServices)
  const enableSkipForMC = Boolean(
    Enable_Skip_Button_For_MC && isNewMC && !shouldRestrictPartnerOpsSkip,
  )
  const isStandaloneNewWFC =
    isNewWFC && !isNewMC && !isNewBC && !isNewKeapMax && !isNewHomeServices
  const enableSkipForPartnerOps =
    isPartnerOps &&
    !shouldRestrictPartnerOpsSkip &&
    (isNewMC || isStandaloneNewWFC || isNewKeapMax)
  const enableSkipForWFCEligibleRoles =
    normalizedRoles.some(role => WFC_SKIP_ELIGIBLE_ROLES.has(role)) &&
    isStandaloneNewWFC
  const enableSkipForLegacyWFCThryvPlan =
    !selectedCentersAndApps && isLegacyWFCSelected && hasLegacyThryvPlan
  const formatDate = dateStr => {
    return dayjs(dateStr.ISODate)
      .tz(dateStr.timeZoneId)
      .format('dddd, MMMM D, YYYY [at] h:mm A')
  }

  const titles = {
    kickoff: 'Thryv Kickoff Call',
    creative: 'creative consultation',
    'thryv-success-services': 'Thryv Success Services Call',
  }

  const isAu = accountInformation?.country === 'AU'

  const isSkipable = useMemo(() => {
    // FULSAS-12295: Thryv Starter onboarding opt-in kickoff call is mandatory
    if (apptData.isThryvStarterOnboarding) {
      return false
    }
    if (selectedCentersAndApps) {
      if (isPartnerOps) {
        return enableSkipForPartnerOps
      }

      if (
        frontendSkipAppointment ||
        enableSkipForMC ||
        enableSkipForWFCEligibleRoles
      ) {
        return true
      }
      //  return false
    }
    if (
      apptData.callType === 'thryv-success-services' &&
      apptData?.displayKickOffCall
    ) {
      return true
    }

    if (enableSkipForLegacyWFCThryvPlan) {
      return true
    }

    if (isAu && (isNewLeadLift || isNewLeadLiftBNI) && !isNewMC) {
      return true
    }
    return false
  }, [
    selectedCentersAndApps,
    isPartnerOps,
    frontendSkipAppointment,
    enableSkipForMC,
    enableSkipForPartnerOps,
    enableSkipForWFCEligibleRoles,
    enableSkipForLegacyWFCThryvPlan,
    apptData,
    isAu,
    isNewLeadLift,
    isNewLeadLiftBNI,
    isNewMC,
  ])

  const getCustomUrl = (
    apptData,
    accountInformation,
    keapAccount,
    is5NewOrderFLow,
    isNewMC,
    alphaSEOFSD,
    seoBillingDate,
  ) => {
    const callTypes = {
      kickoff: 'Onboarding',
      creative: 'Creative',
      'thryv-success-services': 'ThryvSuccessServices',
    }
    let baseUrl = `cc/order/available-times?appointmentType=${
      callTypes[apptData.callType]
    }&country=${apptData.country || accountInformation.country}&keapAccount=${
      keapAccount || false
    }&address=${encodeURIComponent(
      `${accountInformation.address} ${accountInformation.city} ${accountInformation.state}`,
    )}&languagePreference=${apptData.getLanguagePreference(
      apptData.callType,
    )}&is5NewOrderFLow=${is5NewOrderFLow}&isNewMC=${isNewMC}&isAlphaSEOFSD=${
      alphaSEOFSD ? true : false
    }&thryvStarterOnboardingFlag=${
      apptData.isThryvStarterOnboarding ? true : false
    }`

    if (seoBillingDate) {
      baseUrl += `&seoBillingDate=${encodeURIComponent(seoBillingDate)}`
    }

    return baseUrl
  }

  const renderCallDateSection = () => {
    if (apptData.callDate) {
      return (
        <ParagraphText variant="reg" color="thryv-gray-medium-500">
          Your {titles[apptData.callType]} is scheduled for{' '}
          <ParagraphText
            variant="reg"
            color="thryv-black-500"
            className="inline-block"
          >
            <b>
              {formatDate(apptData.callDate)}{' '}
              {dayjs().tz(apptData.callDate.timeZoneId).format('z')}.
            </b>
          </ParagraphText>{' '}
          Please Confirm.
        </ParagraphText>
      )
    } else if (!selectedCentersAndApps) {
      return (
        <>
          <ParagraphText variant="reg" color="thryv-gray-medium-500">
            Schedule your {titles[apptData.callType]}
          </ParagraphText>
          {apptData.displayKickOffCall && (
            <ParagraphText variant="reg" color="thryv-gray-medium-500">
              (Creative Consultations must be scheduled at least one day after
              Thryv Kickoff Call)
            </ParagraphText>
          )}
        </>
      )
    }
    return null
  }

  const renderErrorSection = () => {
    const callVarTexts = {
      kickoff: 'onboardingCallDate',
      creative: 'creativeCallDate',
      'thryv-success-services': 'thryvSuccessServicesCallDate',
    }
    const callVarText = callVarTexts[apptData.callType]
    if (errors?.[callVarText]?.message) {
      return (
        <ParagraphText variant="reg" color="utility-error-danger">
          {errors.creativeCallDate.message}
        </ParagraphText>
      )
    }
    return null
  }

  const renderButtonSection = () => {
    const alphaSEOAdded =
      selectedCentersAndApps?.centers?.find(({name}) => name === 'AEO & SEO') ||
      selectedCentersAndApps?.apps?.find(({name}) => name === 'AEO & SEO')
    const alphaSEOFSD = alphaSEOAdded?.startDate

    let isNewSEO = false
    if (alphaSEOAdded) {
      if (!(alphaSEOAdded?.alreadyIncluded === true)) isNewSEO = true
    }
    let homeServicesAdded = selectedCentersAndApps?.apps?.find(({code}) =>
      [
        'maxhvac-5-thryv-m2m',
        'maxroof-5-thryv-m2m',
        'maxelect-5-thryv-m2m',
        'maxplumb-5-thryv-m2m',
      ].includes(code),
    )
    let isNewHomeServices = false
    if (homeServicesAdded) {
      if (!(homeServicesAdded?.alreadyIncluded === true))
        isNewHomeServices = true
    }
    if (!isNewSEO && !selectedCentersAndApps) {
      const callTypes = {
        kickoff: 'onboarding',
        creative: 'creative',
        'thryv-success-services': 'thryv-success-services',
      }
      const callType = callTypes[apptData.callType]
      let codes = includeAddonsCodes(
        `plans/${callType}/available-times?country=${
          accountInformation.country
        }&keapAccount=${keapAccount || false}&address=${encodeURIComponent(
          accountInformation.address,
        )}%20${accountInformation.city}%20${
          accountInformation.state
        }&software=${apptData.industryCategoryType}`,
        apptData.callType,
      )

      if (
        codes.split(',').find(item => item === 'AEO & SEO') ||
        codes.split('=').find(item => item === 'AEO & SEO')
      ) {
        isNewSEO = true
      }
    }

    // tl mc new logic code
    let isNewThryvLeads = isThryvLeads && isNewTLMCAccessible
    console.log({isNewThryvLeads})
    const modalTitle = {
      kickoff: 'Thryv Kickoff Call',
      creative: 'creative consultation',
      'thryv-success-services': 'Thryv Success Services Call',
    }
    const modalId = {
      kickoff: 'onboarding',
      creative: 'creative',
      'thryv-success-services': 'thryv-success-services',
    }
    const callType4 = {
      kickoff: 'onboarding',
      creative: 'creative',
      'thryv-success-services': 'thryv-success-services',
    }
    //
    if (apptData.callDate) {
      return (
        <>
          <Button
            variant="secondary"
            buttonType="outlined"
            onClick={apptData.resetAppointment}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="ml-2"
            onClick={apptData.confirmAppointment}
            disabled={isLoading}
          >
            Confirm
          </Button>
        </>
      )
    } else if (
      apptData.callType !== 'creative' ||
      shouldEnableCreativeCallModal()
    ) {
      const isKickoff = apptData.callType === 'kickoff'
      const isCreative = apptData.callType === 'creative'
      const isNetNewFlow = is5NewOrderFLow || isNewMC

      let minDate = isNewSEO ? addDays(new Date(), 7) : addDays(new Date(), 2)
      let maxDate = addDays(new Date(), 30)

      if (isCreative && onboardingCallDate?.ISODate) {
        minDate = addDays(new Date(onboardingCallDate.ISODate), 1)
        maxDate = addDays(minDate, 30)
      }

      if (isNewSEO) {
        if (alphaSEOFSD) {
          if (isKickoff) {
            if (isNetNewFlow) {
              minDate = addDays(new Date(), 2)
              maxDate = addDays(new Date(), 21)
            } else {
              minDate = addDays(new Date(), 7)
              maxDate = addDays(new Date(), 30)
            }
          }
          if (isCreative && onboardingCallDate) {
            const baseDate = toValidDate(
              onboardingCallDate?.ISODate,
              new Date(),
            )
            if (isNetNewFlow) {
              minDate = new Date(baseDate)
              maxDate = addDays(new Date(), 30)
            } else {
              minDate = addDays(new Date(), 2)
              maxDate = new Date(baseDate)
            }
          }
        } else {
          if (isCreative && isNetNewFlow) {
            minDate = addDays(new Date(), 2)
            maxDate = addDays(new Date(), 30)
          }
        }
      }

      if (apptData.callType === 'creative' && newSEO4v) {
        minDate = addDays(new Date(), 2)
        maxDate = addDays(new Date(), 30)
      }
      // Pre-5.0 SEO (addon-4-seobase-m2m) kickoff: use API-driven offset when
      // selectedCentersAndApps is falsy (pre-5.0 flow) and isNewSEO is false.
      if (isKickoff && newSEO4v && !isNewSEO) {
        minDate = addDays(new Date(), pre5SeoOffsetStartDay || 2)
      }
      if (apptData.callType === 'kickoff' && seoBillingDate && selectedAddons) {
        if (isMCAlreadyIncluded) {
          // MC already on account (upgrade adding SEO with FSD): book near FSD
          // minDate = toValidDate(
          //   `${seoBillingDate}T00:00:00`,
          //   addDays(new Date(), 2),
          // )
          minDate = addDays(new Date(), pre5SeoOffsetStartDay || 2)
          maxDate = addDays(minDate, 21)
        } else {
          // Net New (MC not on account): book within 48 hours
          minDate = addDays(new Date(), pre5SeoOffsetStartDay || 2)
          maxDate = addDays(new Date(), 21)
        }
      }
      if (
        apptData.callType === 'thryv-success-services' &&
        is5NewOrderFLow &&
        selectedCentersAndApps
      ) {
        minDate = addDays(new Date(), 30)
        maxDate = addDays(new Date(), 60)
      }

      return (
        <div
          style={{
            justifyContent: selectedCentersAndApps ? 'flex-end' : 'block',
            display: 'flex',
            marginTop: selectedCentersAndApps ? '15px' : '0px',
          }}
        >
          {isSkipable && (
            <Button
              onClick={() => {
                apptData.skipAppointment(apptData.callType)
              }}
              variant="text"
              level={1}
              textVariant="light"
              className="mr-3"
            >
              Skip
            </Button>
          )}

          <DateTimeModal
            modalTitle={`Schedule your ${modalTitle[apptData.callType]}`}
            buttonText="schedule"
            modalId={`${modalId[apptData.callType]}-call-modal`}
            callType={apptData.callType}
            saveDateTime={apptData.saveCallData}
            register={register}
            errors={errors}
            address="Chicago"
            customURL={includeAddonsCodes(
              selectedCentersAndApps
                ? getCustomUrl(
                    apptData,
                    accountInformation,
                    keapAccount,
                    is5NewOrderFLow,
                    isNewMC,
                    alphaSEOFSD,
                    seoBillingDate,
                  )
                : `plans/${
                    callType4[apptData.callType]
                  }/available-times?country=${
                    accountInformation.country
                  }&keapAccount=${
                    keapAccount || false
                  }&address=${encodeURIComponent(
                    accountInformation.address,
                  )}%20${accountInformation.city}%20${
                    accountInformation.state
                  }${
                    apptData.callType === 'kickoff'
                      ? `&languagePreference=${apptData.getLanguagePreference(
                          apptData.callType,
                        )}`
                      : ''
                  }&selectedProduct=${
                    accountInformation?.selectedPackage
                  }&software=${apptData.industryCategoryType}${
                    seoBillingDate
                      ? `&seoBillingDate=${encodeURIComponent(seoBillingDate)}`
                      : ''
                  }`,
              apptData.callType,
            )}
            defaultDate={defaultDate()}
            isCreativeCall={apptData.callType === 'creative'}
            isThryvSuccessServicesCall={
              apptData.callType === 'thryv-success-services'
            }
            isMCSelected={isMCSelected}
            radioLanguageOptions={RADIO_LANGUAGE_PREFERENCES}
            setLanguagePreference={apptData.setLanguagePreference}
            country={accountInformation.country}
            isV5={selectedCentersAndApps ? true : false}
            isRestrictedAccessHIPPASelected={isRestrictedAccessHIPPASelected}
            noneCustomVideoCreativeItem={noneCustomVideoCreativeItem}
            customServiceId={selectedService?.value}
            isUsSpanishOptionAvailable={isUsSpanishOptionAvailable}
            isKickoff={apptData.callType === 'kickoff'}
            isThryvStarterOnboarding={apptData.isThryvStarterOnboarding}
            isThryvLeadsOrSeoIncluded={isThryvLeadsOrSeoIncluded}
            is5Order={!!selectedCentersAndApps}
            disabled={Object.keys(errors).length > 0}
            onboardingCallDate={onboardingCallDate}
            isNewSEO={isNewSEO}
            isNewHomeServices={isNewHomeServices}
            minDate={minDate}
            maxDate={maxDate}
            disableWeekends={apptData.callType === 'thryv-success-services'}
          />
        </div>
      )
    } else {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            flexDirection: 'column',
            marginTop: '10px',
          }}
        >
          <Button
            className="mt-2"
            disabled={true}
            variant="primary"
            style={{height: 33.6}}
          >
            Schedule
          </Button>
        </div>
      )
    }
  }

  const renderSpinnerSection = () => {
    if (
      (apptData.callType === 'kickoff' &&
        isLoading &&
        apptId !== null &&
        apptData.callDate) ||
      (isLoading && !apptData.displayKickOffCall && apptData.callDate)
    ) {
      return <Spinner />
    } else if (
      (apptData.callType === 'creative' &&
        isLoading &&
        apptId !== null &&
        apptData.callDate) ||
      (isLoading && !apptData.displayCreativeCall && apptData.callDate)
    ) {
      return <Spinner />
    } else if (
      (apptData.callType === 'thryv-success-services' &&
        isLoading &&
        apptId !== null &&
        apptData.callDate) ||
      (isLoading &&
        !apptData.displayThryvSuccessServicesCall &&
        apptData.callDate)
    ) {
      return <Spinner />
    }
    return null
  }

  return (
    <>
      {renderCallDateSection()}
      {renderErrorSection()}
      {renderButtonSection()}
      {renderSpinnerSection()}
    </>
  )
}
